import { useEffect } from 'react';
import type { RootState } from '@/store';
import {
  Box,
  Button,
  CardContent,
  Container,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import {
  getLoanProgramsForMode,
  USER_LOAN_PROGRAM,
  userLoanProgramMap,
} from '@/config/constants';
import {
  useCreateProductMutation,
  useGetProductQuery,
  useUpdateProductMutation,
} from '@/apis/extended/productApi';
import { GlassCard } from '@/components/ui/GlassCard';
import {
  setCurrentLoanField,
  setNewLoanField,
  setPropertyField,
  setRefinanceSelection,
  setRefinanceState,
} from '@/store/slices/refinanceSlice';
import {
  getRefiTypeOptions,
  normalizeRefiType,
  REFI_TYPE_LABELS,
  type RefiType,
} from '@/utils/refinanceDocs';
import type { Product } from '@/types/ProductType';

const parseNumber = (value: string) => {
  if (value === '') return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

function mapProductToRefinanceState(product: Product) {
  const addr = product.address ?? {};
  const ref = product.refinance ?? {};
  const cur = ref.currentLoan ?? {};
  const prop = ref.property ?? {};
  const newL = ref.newLoan ?? {};
  const sel = ref.selection ?? {};
  return {
    currentLoan: {
      rate: cur.rate ?? 6.75,
      balance: cur.balance ?? 250000,
      termYears: cur.termYears ?? 30,
      monthlyPayment: cur.monthlyPayment ?? 1800,
      escrowTaxes: cur.escrowTaxes ?? 300,
      escrowInsurance: cur.escrowInsurance ?? 120,
      escrowHoa: cur.escrowHoa ?? 0,
    },
    property: {
      address: prop.street ?? addr.street ?? '',
      city: prop.city ?? addr.city ?? '',
      state: prop.state ?? addr.state ?? '',
      zipCode: (prop.zip ?? addr.zip ?? '') as string,
      value: prop.value ?? product.price ?? 0,
      propertyType: prop.propertyType ?? 'Single Family',
    },
    newLoan: {
      termYears: newL.termYears ?? 30,
      creditScore: newL.creditScore ?? 720,
      cashOutAmount: newL.cashOutAmount ?? 0,
      requestedLoanAmount: newL.requestedLoanAmount ?? 0,
    },
    selection: {
      program:
        (sel.program as USER_LOAN_PROGRAM) ?? USER_LOAN_PROGRAM.CONVENTIONAL,
      refiType: (sel.refiType as RefiType) ?? 'rate_and_term',
    },
  };
}

const inputSx = (theme: ReturnType<typeof useTheme>) => ({
  '& .MuiOutlinedInput-root': {
    color: theme.palette.text.primary,
    '& fieldset': { borderColor: theme.palette.greyShades.border1 },
    '&:hover fieldset': {
      borderColor: theme.palette.text.secondary,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': { color: theme.palette.text.secondary },
  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
  '& .MuiSelect-icon': { color: theme.palette.text.secondary },
});

export const RefinanceIntakePage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { productId } = useParams<{ productId: string }>();
  const isEditMode = Boolean(productId);
  const [createProduct, { isLoading: isCreateLoading }] =
    useCreateProductMutation();
  const [updateProduct, { isLoading: isUpdateLoading }] =
    useUpdateProductMutation();
  const { data: product } = useGetProductQuery(productId!, {
    skip: !productId,
  });
  const refinance = useSelector((state: RootState) => state.refinance);
  const isSaving = isCreateLoading || isUpdateLoading;

  const loanPrograms = getLoanProgramsForMode('refinance', false);
  const refiTypeOptions = getRefiTypeOptions(refinance.selection.program);
  const isSaveDisabled =
    !refinance.property.address ||
    !refinance.property.city ||
    !refinance.property.state ||
    !refinance.property.zipCode ||
    refinance.property.value <= 0;

  useEffect(() => {
    if (product && productId) {
      dispatch(setRefinanceState(mapProductToRefinanceState(product)));
    }
  }, [dispatch, product, productId]);

  useEffect(() => {
    const normalized = normalizeRefiType(
      refinance.selection.program,
      refinance.selection.refiType as RefiType
    );
    if (normalized !== refinance.selection.refiType) {
      dispatch(setRefinanceSelection({ refiType: normalized }));
    }
  }, [dispatch, refinance.selection.program, refinance.selection.refiType]);

  const buildRefinancePayload = () => {
    const fullAddress = `${refinance.property.address}, ${refinance.property.city}, ${refinance.property.state} ${refinance.property.zipCode}`;
    return {
      name: fullAddress,
      price: refinance.property.value,
      address: {
        street: refinance.property.address,
        city: refinance.property.city,
        state: refinance.property.state,
        zip: refinance.property.zipCode,
        fullAddress,
      },
      refinance: {
        currentLoan: refinance.currentLoan,
        property: {
          street: refinance.property.address,
          city: refinance.property.city,
          state: refinance.property.state,
          zip: refinance.property.zipCode,
          fullAddress,
          value: refinance.property.value,
          propertyType: refinance.property.propertyType,
        },
        newLoan: refinance.newLoan,
        selection: refinance.selection,
      },
    };
  };

  const handleSaveRefinance = async () => {
    if (isSaveDisabled) return;
    const payload = buildRefinancePayload();
    try {
      if (isEditMode && productId) {
        await updateProduct({
          id: productId,
          ...payload,
        }).unwrap();
        navigate(`/refinance/summary/${productId}`);
      } else {
        const product = await createProduct({
          ...payload,
          transactionMode: 'refinance',
        }).unwrap();
        if (product?._id) {
          navigate(`/refinance/summary/${product._id}`);
        }
      }
    } catch (error) {
      console.error('Failed to save refinance product:', error);
    }
  };

  const cardContentSx = {
    p: { xs: 2, sm: 3, md: 4 },
    color: 'text.primary',
  };

  const sectionCardSx = {
    borderRadius: 3,
    border: `1px solid ${theme.palette.greyShades.border1}`,
    color: 'text.primary',
    boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 4,
        px: { xs: 2, sm: 3, md: 4 },
        overflow: 'hidden',
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
          }}
        >
          {isEditMode ? 'Edit refinance intake' : 'Refinance Intake'}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <GlassCard variant="medium" sx={sectionCardSx}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '1rem', sm: '1.125rem' },
                px: { xs: 2, sm: 3, md: 4 },
                pt: { xs: 2, sm: 3, md: 4 },
                pb: 1,
              }}
            >
              Property + Refinance Goal
            </Typography>
            <CardContent sx={cardContentSx}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Street Address"
                    fullWidth
                    value={refinance.property.address}
                    onChange={(e) =>
                      dispatch(
                        setPropertyField({
                          field: 'address',
                          value: e.target.value,
                        })
                      )
                    }
                    sx={inputSx(theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Property Value"
                    type="number"
                    fullWidth
                    value={refinance.property.value}
                    onChange={(e) =>
                      dispatch(
                        setPropertyField({
                          field: 'value',
                          value: parseNumber(e.target.value),
                        })
                      )
                    }
                    sx={inputSx(theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="City"
                    fullWidth
                    value={refinance.property.city}
                    onChange={(e) =>
                      dispatch(
                        setPropertyField({
                          field: 'city',
                          value: e.target.value,
                        })
                      )
                    }
                    sx={inputSx(theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="State"
                    fullWidth
                    value={refinance.property.state}
                    onChange={(e) =>
                      dispatch(
                        setPropertyField({
                          field: 'state',
                          value: e.target.value,
                        })
                      )
                    }
                    sx={inputSx(theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Zip Code"
                    fullWidth
                    value={refinance.property.zipCode}
                    onChange={(e) =>
                      dispatch(
                        setPropertyField({
                          field: 'zipCode',
                          value: e.target.value,
                        })
                      )
                    }
                    sx={inputSx(theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Property Type"
                    fullWidth
                    value={refinance.property.propertyType}
                    onChange={(e) =>
                      dispatch(
                        setPropertyField({
                          field: 'propertyType',
                          value: e.target.value,
                        })
                      )
                    }
                    sx={inputSx(theme)}
                  >
                    {[
                      'Single Family',
                      'Condo',
                      'Townhome',
                      'Multi-family',
                      'Other',
                    ].map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Loan Program"
                    fullWidth
                    value={refinance.selection.program}
                    onChange={(e) =>
                      dispatch(
                        setRefinanceSelection({
                          program: e.target.value as USER_LOAN_PROGRAM,
                        })
                      )
                    }
                    sx={inputSx(theme)}
                  >
                    {loanPrograms.map((program) => (
                      <MenuItem key={program} value={program}>
                        {userLoanProgramMap[program]}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Refinance Type"
                    fullWidth
                    value={refinance.selection.refiType}
                    onChange={(e) =>
                      dispatch(
                        setRefinanceSelection({
                          refiType: e.target.value as RefiType,
                        })
                      )
                    }
                    sx={inputSx(theme)}
                  >
                    {refiTypeOptions.map((type) => (
                      <MenuItem key={type} value={type}>
                        {REFI_TYPE_LABELS[type]}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Desired New Term (Years)"
                    type="number"
                    fullWidth
                    value={refinance.newLoan.termYears}
                    onChange={(e) =>
                      dispatch(
                        setNewLoanField({
                          field: 'termYears',
                          value: parseNumber(e.target.value),
                        })
                      )
                    }
                    sx={inputSx(theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Credit Score"
                    type="number"
                    fullWidth
                    value={refinance.newLoan.creditScore}
                    onChange={(e) =>
                      dispatch(
                        setNewLoanField({
                          field: 'creditScore',
                          value: parseNumber(e.target.value),
                        })
                      )
                    }
                    sx={inputSx(theme)}
                  />
                </Grid>
                {refinance.selection.refiType === 'cash_out' && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Cash-Out Amount"
                      type="number"
                      fullWidth
                      value={refinance.newLoan.cashOutAmount}
                      onChange={(e) =>
                        dispatch(
                          setNewLoanField({
                            field: 'cashOutAmount',
                            value: parseNumber(e.target.value),
                          })
                        )
                      }
                      sx={inputSx(theme)}
                    />
                  </Grid>
                )}
                {['heloc', 'heloan', 'second_mortgage'].includes(
                  refinance.selection.refiType
                ) && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Requested Loan Amount"
                      type="number"
                      fullWidth
                      value={refinance.newLoan.requestedLoanAmount}
                      onChange={(e) =>
                        dispatch(
                          setNewLoanField({
                            field: 'requestedLoanAmount',
                            value: parseNumber(e.target.value),
                          })
                        )
                      }
                      sx={inputSx(theme)}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </GlassCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <GlassCard variant="medium" sx={sectionCardSx}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '1rem', sm: '1.125rem' },
                px: { xs: 2, sm: 3, md: 4 },
                pt: { xs: 2, sm: 3, md: 4 },
                pb: 1,
              }}
            >
              Current Loan
            </Typography>
            <CardContent sx={cardContentSx}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Current Rate (%)"
                    type="number"
                    fullWidth
                    value={refinance.currentLoan.rate}
                    onChange={(e) =>
                      dispatch(
                        setCurrentLoanField({
                          field: 'rate',
                          value: parseNumber(e.target.value),
                        })
                      )
                    }
                    sx={inputSx(theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Current Balance"
                    type="number"
                    fullWidth
                    value={refinance.currentLoan.balance}
                    onChange={(e) =>
                      dispatch(
                        setCurrentLoanField({
                          field: 'balance',
                          value: parseNumber(e.target.value),
                        })
                      )
                    }
                    sx={inputSx(theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Remaining Term (Years)"
                    type="number"
                    fullWidth
                    value={refinance.currentLoan.termYears}
                    onChange={(e) =>
                      dispatch(
                        setCurrentLoanField({
                          field: 'termYears',
                          value: parseNumber(e.target.value),
                        })
                      )
                    }
                    sx={inputSx(theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Monthly Payment (P&I)"
                    type="number"
                    fullWidth
                    value={refinance.currentLoan.monthlyPayment}
                    onChange={(e) =>
                      dispatch(
                        setCurrentLoanField({
                          field: 'monthlyPayment',
                          value: parseNumber(e.target.value),
                        })
                      )
                    }
                    sx={inputSx(theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Monthly Taxes"
                    type="number"
                    fullWidth
                    value={refinance.currentLoan.escrowTaxes}
                    onChange={(e) =>
                      dispatch(
                        setCurrentLoanField({
                          field: 'escrowTaxes',
                          value: parseNumber(e.target.value),
                        })
                      )
                    }
                    sx={inputSx(theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Monthly Insurance"
                    type="number"
                    fullWidth
                    value={refinance.currentLoan.escrowInsurance}
                    onChange={(e) =>
                      dispatch(
                        setCurrentLoanField({
                          field: 'escrowInsurance',
                          value: parseNumber(e.target.value),
                        })
                      )
                    }
                    sx={inputSx(theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Monthly HOA"
                    type="number"
                    fullWidth
                    value={refinance.currentLoan.escrowHoa}
                    onChange={(e) =>
                      dispatch(
                        setCurrentLoanField({
                          field: 'escrowHoa',
                          value: parseNumber(e.target.value),
                        })
                      )
                    }
                    sx={inputSx(theme)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </GlassCard>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3, borderColor: 'divider' }} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleSaveRefinance}
          disabled={isSaveDisabled || isSaving}
          sx={{ fontWeight: 600, borderRadius: 2 }}
        >
          Save Refinance
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/refinance')}
          sx={{ fontWeight: 600, borderRadius: 2 }}
        >
          Continue to Summary
        </Button>
      </Box>
    </Container>
  );
};
